import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import { useUser } from "../contexts/UserContext";
import { useUserProfiles } from "../contexts/UserProfileContext";
import { GAMES } from "../config/games";
import HeroButton from "../components/HeroButton";
import GameTile from "../components/GameTile";
import LoginModal from "../components/LoginModal";
import DreamModal from "../components/DreamModal";
import SubscriptionManager from "../components/SubscriptionManager";
import HolidayBanner from "../components/HolidayBanner";
import {
  FaCrown,
  FaCheckCircle,
} from "react-icons/fa";
import ConfettiExplosion from "react-confetti-explosion";
import {
  hasHardwareKeyboard,
  getDeviceType,
  isKeyboardSuitableDevice,
} from "../utils/browserDetection";
import "./GameSelection.css";

function GameSelection() {
  const {
    user,
    subscriptionStatus,
    refreshSubscriptionStatus,
    signInWithGoogle,
  } = useUser();
  const navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Device detection
  const [deviceInfo, setDeviceInfo] = useState({
    hasKeyboard: false,
    deviceType: "unknown",
    isKeyboardSuitable: false,
  });

  // Initialize device detection
  useEffect(() => {
    const detectDevice = () => {
      setDeviceInfo({
        hasKeyboard: hasHardwareKeyboard(),
        deviceType: getDeviceType(),
        isKeyboardSuitable: isKeyboardSuitableDevice(),
      });
    };

    detectDevice();

    // Re-detect on window resize (for orientation changes, etc.)
    window.addEventListener("resize", detectDevice);
    return () => window.removeEventListener("resize", detectDevice);
  }, []);

  // Filter games for December
  const isDecember = new Date().getMonth() === 11;
  const games = GAMES.filter((game) => !game.showInDecemberOnly || isDecember)
    .filter((game) => !game.hidden)
    .filter(
      // Filter out GameDev if no hardware keyboard
      (game) => game.name !== "Game Developer" || deviceInfo.hasKeyboard
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Separate games into free and premium
  const freeGames = games.filter((game) => game.isFree);
  const premiumGames = games.filter((game) => !game.isFree);

  // Determine if user has subscription access
  const hasSubscription = subscriptionStatus?.status === "active";
  const isAuthenticated = !!user;
  const isLoadingSubscription = isAuthenticated && !subscriptionStatus;

  // Force re-render when subscription status changes
  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [subscriptionStatus, hasSubscription, isLoadingSubscription]);

  // Monitor subscription status changes and show thank you when user becomes subscribed
  useEffect(() => {
    // Check if we just became subscribed (from false to true)
    if (hasSubscription && !isLoadingSubscription && user) {
      // Check URL for success parameter to confirm this is from a successful purchase
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get("success");

      if (success === "true") {
        setShowThankYou(true);
      }
    }
  }, [hasSubscription, isLoadingSubscription, user]);

  // Check for success parameter and show thank you modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const force = urlParams.get("force");
    const portal = urlParams.get("portal");

    if (success === "true" || force === "true") {
      // If force=true, show modal immediately
      if (force === "true") {
        setShowThankYou(true);
      }

      // Check subscription status immediately with force refresh
      if (
        refreshSubscriptionStatus &&
        typeof refreshSubscriptionStatus === "function"
      ) {
        refreshSubscriptionStatus(true)
          .then((result) => {
            console.log(
              "[Subscription] Force refresh result after purchase:",
              result
            );
          })
          .catch((error) => {
            console.error(
              "[Subscription] Force refresh error after purchase:",
              error
            );
          });
      }
    } else if (portal === "true") {
      // User returned from billing portal - refresh subscription status
      // Clear the portal parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      if (refreshSubscriptionStatus) {
        refreshSubscriptionStatus()
          .then((result) => {
            console.log(
              "[Subscription] Force refresh result after portal:",
              result
            );
          })
          .catch((error) => {
            console.error(
              "[Subscription] Force refresh error after portal:",
              error
            );
          });
      }
    }
  }, [refreshSubscriptionStatus, user]);

  // Listen for subscription modal events from other components
  useEffect(() => {
    const handleOpenSubscriptionModal = () => {
      setShowSubscriptionModal(true);
    };

    window.addEventListener(
      "openSubscriptionModal",
      handleOpenSubscriptionModal
    );

    return () => {
      window.removeEventListener(
        "openSubscriptionModal",
        handleOpenSubscriptionModal
      );
    };
  }, []);

  const handleDismissThankYou = () => {
    setShowThankYou(false);
    // Clean up URL parameters when modal is dismissed
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("force") || urlParams.get("success")) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleNavigateToGreetingCards = () => {
    // Find the greeting card game and navigate to it
    const greetingCardGame = games.find(
      (game) =>
        game.name.toLowerCase().includes("greeting") ||
        game.component === "GreetingCardStore"
    );

    console.log("[Debug] Searching for greeting card game in:", games.map(g => g.name));
    console.log("[Debug] Found greeting card game:", greetingCardGame);

    if (greetingCardGame && greetingCardGame.path) {
      console.log("[Debug] Navigating to:", greetingCardGame.path);
      navigate(greetingCardGame.path);
    } else {
      console.error("[Debug] Could not find greeting card game or path missing");
    }
  };

  const handleGoogleSignIn = async () => {
    setLoginError("");
    setIsLoginLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        setShowLoginModal(false);
      } else {
        setLoginError(result.error);
      }
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const renderGameTile = (game, index) => {
    if (!isAuthenticated) {
      // Unauthenticated view - show login prompt
      return (
        <GameTile
          key={game.name}
          game={game}
          index={index}
          isLocked={true}
          onPlay={() => {
            setShowFeatures(false); // Use simple login modal for game-specific sign-ins
            setShowLoginModal(true);
          }}
          showSignInPrompt={true}
          hasSubscription={hasSubscription}
        />
      );
    }

    // Authenticated view
    const isLocked = !game.isFree && !hasSubscription;

    return (
      <GameTile
        key={game.name}
        game={game}
        index={index}
        isLocked={isLocked}
        onPlay={isLocked ? () => setShowSubscriptionModal(true) : undefined}
        hasSubscription={hasSubscription}
      />
    );
  };

  const getHeaderText = () => {
    if (!isAuthenticated) {
      return {
        title: "Available Games",
        subtitle:
          "Sign in to play our free games or subscribe to unlock the full collection!",
      };
    }

    return {
      title: "Choose Your Dream Job!",
      subtitle: hasSubscription
        ? "Enjoy unlimited access to all our amazing games!"
        : `Start with our free games or unlock all ${games.length} games with a family subscription`,
    };
  };

  const { title, subtitle } = getHeaderText();

  return (
    <div className="game-selection">
      <div className="game-selection-header">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>

      {/* Holiday Banner */}
      <HolidayBanner
        onNavigateToGreetingCards={handleNavigateToGreetingCards}
        hasSubscription={hasSubscription}
      />

      {!isAuthenticated ? (
        /* Unauthenticated Layout */
        <>
          {/* Free Games Section */}
          <div className="games-section">
            <div className="section-header">
              <h2 className="section-title free-section">Free Games</h2>
            </div>
            <div className="game-grid">
              {freeGames.map((game, index) => renderGameTile(game, index))}
            </div>
          </div>

          {/* Premium Games Section */}
          <div className="games-section">
            <div className="section-header">
              <h2 className="section-title premium-section">
                Premium Games ({premiumGames.length})
              </h2>
              <p className="section-description">
                Unlock these amazing games with a subscription
              </p>
              <HeroButton
                icon={FaCrown}
                onClick={() => {
                  setShowLoginModal(true);
                  setShowFeatures(true);
                }}
                className="section-cta-button"
                variant="secondary"
              >
                Subscribe to Unlock All Games
              </HeroButton>
            </div>
            <div className="game-grid premium-grid">
              {premiumGames.map((game, index) =>
                renderGameTile(game, index + freeGames.length)
              )}
            </div>
          </div>
        </>
      ) : isLoadingSubscription ? (
        /* Loading Subscription Status */
        <div className="games-section">
          <div className="loading-subscription">
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h4>Loading your games...</h4>
              <p className="text-muted">Checking your subscription status</p>
            </div>
          </div>
        </div>
      ) : hasSubscription ? (
        /* Subscribed Layout - All Games Unified */
        <div className="games-section">
          <div className="game-grid">
            {games.map((game, index) => renderGameTile(game, index))}
          </div>
        </div>
      ) : (
        /* Authenticated but No Subscription Layout OR Loading */
        <>
          {/* Free Games Section */}
          <div className="games-section">
            <div className="section-header">
              <h2 className="section-title free-section">Free Games</h2>
              {!isAuthenticated && (
                <p className="section-description">
                  Sign in to play our free games or subscribe to unlock the full
                  collection!
                </p>
              )}
            </div>
            <div className="game-grid">
              {freeGames.map((game, index) => renderGameTile(game, index))}
            </div>
          </div>

          {/* Premium Games Section */}
          <div className="games-section">
            <div className="section-header">
              <h2 className="section-title premium-section">Premium Games</h2>
              <p className="section-description">
                Unlock these amazing games with a subscription
              </p>
              <div className="section-action">
                <HeroButton
                  icon={FaCrown}
                  onClick={() => setShowSubscriptionModal(true)}
                  className="unlock-all-button"
                >
                  Unlock All Games
                </HeroButton>
              </div>
            </div>
            <div className="game-grid premium-grid">
              {premiumGames.map((game, index) =>
                renderGameTile(game, index + freeGames.length)
              )}
            </div>
          </div>
        </>
      )}

      {/* Login Modal */}
      <LoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onGoogleSignIn={handleGoogleSignIn}
        error={loginError}
        isLoading={isLoginLoading}
        showFeatures={showFeatures}
      />

      {/* Thank You Modal */}
      <DreamModal
        show={showThankYou}
        onHide={handleDismissThankYou}
        title={
          <>
            <FaCheckCircle className="me-2 text-success" />
            Welcome to Star Launch Premium!
          </>
        }
        className="thank-you-modal"
        size="lg"
      >
        <div className="thank-you-content">
          <div className="text-center mb-4">
            <h3 className="mb-3 text-white fw-bold">
              All Premium Games Are Now Unlocked! 🎮
            </h3>
          </div>

          <div className="educational-content">
            <div className="mb-4">
              <div className="d-flex align-items-start mb-3">
                <span className="me-3 fs-4">🌟</span>
                <div>
                  <h5 className="text-warning mb-2">
                    Let Your Imagination Soar!
                  </h5>
                  <p className="text-white mb-0">
                    Star Launch is designed to spark creativity and help
                    children explore different careers through imaginative play.
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-start mb-3">
                <span className="me-3 fs-4">🎭</span>
                <div>
                  <h5 className="text-warning mb-2">Role-Playing Adventures</h5>
                  <p className="text-white mb-0">
                    Each game lets kids step into exciting roles - from
                    astronauts exploring space to chefs creating delicious
                    meals. Watch as they develop problem-solving skills,
                    empathy, and confidence!
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-start mb-4">
                <span className="me-3 fs-4">🎯</span>
                <div>
                  <h5 className="text-warning mb-2">Educational & Fun</h5>
                  <p className="text-white mb-0">
                    While having a blast, children learn real-world skills,
                    explore career possibilities, and discover their passions in
                    a safe, encouraging environment.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="encouragement-section p-4 rounded-3 border border-light border-2"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
            >
              <div className="d-flex align-items-center mb-3">
                <span className="me-2 fs-4">🚀</span>
                <h5 className="text-info mb-0 fw-bold">
                  Ready to Explore? Here's what to try:
                </h5>
              </div>
              <ul className="list-unstyled mb-0">
                <li className="d-flex align-items-start mb-2">
                  <span className="me-2 text-success">✓</span>
                  <span className="text-white">
                    Start with games that match your child's interests
                  </span>
                </li>
                <li className="d-flex align-items-start mb-2">
                  <span className="me-2 text-success">✓</span>
                  <span className="text-white">
                    Encourage storytelling about their adventures
                  </span>
                </li>
                <li className="d-flex align-items-start mb-2">
                  <span className="me-2 text-success">✓</span>
                  <span className="text-white">
                    Ask "What would you do next?" to spark creativity
                  </span>
                </li>
                <li className="d-flex align-items-start">
                  <span className="me-2 text-success">✓</span>
                  <span className="text-white">
                    Celebrate their imaginative solutions!
                  </span>
                </li>
              </ul>
            </div>

            <div
              className="text-center mt-4 p-3 rounded border border-success"
              style={{ backgroundColor: "rgba(40,167,69,0.2)" }}
            >
              <p className="text-light mb-0 fw-medium">
                💚 Thank you for supporting educational gaming! Your
                subscription helps us create more amazing adventures for young
                minds.
              </p>
            </div>
          </div>
        </div>
      </DreamModal>

      {/* Subscription Modal */}
      <DreamModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        title={
          <>
            <FaCrown className="me-2" />
            Unlock All Games
          </>
        }
        className="subscription-modal"
        size="md"
      >
        <SubscriptionManager />
      </DreamModal>

      {/* Confetti rendered using portal to document.body to ensure it appears above everything */}
      {showThankYou &&
        ReactDOM.createPortal(
          <div className="confetti-container">
            <ConfettiExplosion
              force={0.6}
              duration={4000}
              particleCount={200}
              width={2000}
              height={1200}
              colors={[
                "#ff69b4",
                "#9370db",
                "#ffd700",
                "#ff6b6b",
                "#4ecdc4",
                "#45b7d1",
              ]}
              particleSize={12}
              floorHeight={0}
            />
          </div>,
          document.body
        )}
    </div>
  );
}

export default GameSelection;
